/**
 * Utility functions for device-related operations
 */

interface DeviceWithType {
  deviceType?: {
    name?: string;
    category?: string;
    communicationTechnology?: string;
    deviceRole?: string;
  };
}

export function isLoRaWANDevice(device: DeviceWithType): boolean {
  if (device.deviceType?.communicationTechnology) {
    return device.deviceType.communicationTechnology === "lorawan";
  }
  // Fallback heuristic for data that hasn't been backfilled yet
  const cat = device.deviceType?.category?.toLowerCase() || "";
  return cat.includes("lorawan") || cat.includes("lora");
}

export function isBLEDevice(device: DeviceWithType): boolean {
  if (device.deviceType?.communicationTechnology) {
    return device.deviceType.communicationTechnology === "ble";
  }
  // Fallback heuristic
  const cat = device.deviceType?.category?.toLowerCase() || "";
  return cat.includes("xtel");
}

export function isGateway(device: DeviceWithType): boolean {
  if (device.deviceType?.deviceRole) {
    return device.deviceType.deviceRole === "gateway";
  }
  // Fallback heuristic
  const name = device.deviceType?.name?.toLowerCase() || "";
  return name.includes("xentral") || name.includes("gateway");
}

/**
 * Normalise a device's `externalTags` (jsonb) into a flat list of display labels.
 *
 * The backend canonicalises external tags to `Record<string, string>` where plain
 * tags (the common case) become `{ "cust:Acme": "" }` — the meaningful text lives in
 * the KEY and the value is empty. Naively rendering the value yields blank badges, so
 * we prefer a non-empty value but fall back to the key. Also tolerates the legacy
 * `string[]` shape.
 */
export function externalTagsToLabels(
  externalTags?: Record<string, unknown> | string[] | null,
): string[] {
  if (!externalTags) return [];

  if (Array.isArray(externalTags)) {
    return externalTags.filter((t): t is string => typeof t === "string" && t.length > 0);
  }

  if (typeof externalTags === "object") {
    return Object.entries(externalTags).map(([key, value]) => {
      const str = value == null ? "" : String(value);
      return str.length > 0 ? str : key;
    });
  }

  return [];
}

/**
 * Derive the 6-digit Mini short ID (e.g. "000123") from a device externalId.
 * Xtellio Minis carry a devEUI of the form `0262XXXXXX`; the trailing six
 * characters are the human-facing short code printed on the device.
 * Returns null for non-Mini identifiers.
 */
export function getMiniShortId(externalId?: string | null): string | null {
  if (!externalId) return null;
  const match = /^0262([0-9A-Fa-f]{6})$/.exec(externalId.trim());
  return match ? match[1].toUpperCase() : null;
}

/**
 * @deprecated Use isLoRaWANDevice() instead. Kept for backward compatibility.
 */
export function isCharpMaxiLoRaWANDevice(device: DeviceWithType): boolean {
  if (device.deviceType?.communicationTechnology) {
    return device.deviceType.communicationTechnology === "lorawan";
  }
  const deviceTypeName = device.deviceType?.name?.toLowerCase() || "";
  const deviceTypeCategory = device.deviceType?.category?.toLowerCase() || "";
  return (
    deviceTypeName.includes("maxi") &&
    (deviceTypeCategory.includes("lorawan") || deviceTypeCategory.includes("lora"))
  );
}
